import React from 'react';
import { UserIcon, HomeIcon, FileTextIcon, SearchIcon, LightbulbIcon, SettingsIcon, CheckCircle2Icon, ArrowRightIcon, ArrowDownIcon, SparklesIcon, CreditCardIcon, MapPinIcon, DatabaseIcon, BookOpenIcon } from 'lucide-react';
import { ATNA_LOGO, USER_AVATAR, atnaHowCopy as c, atnaSteps, actFlow, type Bi, type StepKey } from '../../../data/atnaHow';
const STEP_ICONS: Record<StepKey, typeof UserIcon> = {
  identify: UserIcon,
  context: HomeIcon,
  understand: FileTextIcon,
  verify: SearchIcon,
  recommend: LightbulbIcon,
  act: SettingsIcon
};
// Same icon language as the TAME source list, so "Vérifie" visibly feeds TAME.
const SOURCE_MINI = [FileTextIcon, MapPinIcon, DatabaseIcon, BookOpenIcon];
const ACT_ICONS = [FileTextIcon, CreditCardIcon, CheckCircle2Icon];
type Pick = (b: Bi) => string;
export function IntentBubble({
  pick
}: {
  pick: Pick;
}) {
  return <div className="flex items-center gap-4 min-w-0" data-id="element-1046" data-mp="1wnhfacltj2wz">
      <img src={USER_AVATAR} alt="" className="w-[48px] h-[48px] rounded-full object-cover border-[3px] border-white shadow-[0_8px_20px_-10px_rgba(18,26,58,0.4)] shrink-0" data-id="element-1047" data-mp="316brf578rp6x" />
      <div className="flex items-center gap-3 rounded-2xl border border-[#E7E6F5] bg-white px-5 py-3 shadow-[0_12px_30px_-18px_rgba(79,70,229,0.35)] min-w-0" data-id="element-1048" data-mp="2rgihe8mqzi4s">
        <span className="font-sans text-[15.5px] md:text-[16.5px] text-[#121A3A] whitespace-nowrap" data-id="element-1049" data-mp="2ngi3qq7h3ays">{pick(c.intent)}</span>
        <SparklesIcon className="w-5 h-5 text-[#6D66F0] shrink-0" strokeWidth={1.6} aria-hidden="true" data-id="element-1050" data-mp="3k2trqb05oxes" />
      </div>
    </div>;
}
export function AtnaOrb({
  pick,
  size = 'lg'
}: {
  pick: Pick;
  size?: 'lg' | 'sm';
}) {
  const dim = size === 'lg' ? 'w-[160px] h-[160px]' : 'w-[144px] h-[144px]';
  return <div className={`relative ${dim} shrink-0`} data-id="element-1051" data-mp="367rbatxonpnq">
      <div className="absolute inset-[-16px] rounded-full bg-[#E4E2FB] opacity-60 blur-2xl" aria-hidden="true" data-id="element-1052" data-mp="1zqdxeb2wgx43" />
      <div className="relative w-full h-full rounded-full bg-white border border-[#E7E6F8] shadow-[0_24px_60px_-28px_rgba(79,70,229,0.55),inset_0_0_40px_rgba(228,226,251,0.7)] flex flex-col items-center justify-center gap-2.5" data-id="element-1053" data-mp="1uoomcvfogjg0">
        <img src={ATNA_LOGO} alt="ATNA" className="h-[44px] w-auto object-contain" data-id="element-1054" data-mp="2145m5g79mavo" />
        <p className="font-sans text-[10px] md:text-[11px] font-medium tracking-[0.28em] uppercase text-[#5A6180] text-center leading-[1.6]" data-id="element-1055" data-mp="1n6h38dwrddt9">
          {c.coreLabel.map(l => <span key={l.fr} className="block" data-id="element-1056" data-mp="1dg5nlu3ppg6j">{pick(l)}</span>)}
        </p>
      </div>
    </div>;
}
function StepCard({
  step,
  index,
  pick
}: {
  step: (typeof atnaSteps)[number];
  index: number;
  pick: Pick;
}) {
  const Icon = STEP_ICONS[step.key];
  return <article className="h-full rounded-2xl border border-[#ECEBF5] bg-white px-4 pt-3.5 pb-3 shadow-[0_14px_34px_-24px_rgba(18,26,58,0.28)] flex flex-col" data-id="element-1057" data-mp="18hb4er1u1plw">
      <div className="flex items-center gap-2.5" data-id="element-1058" data-mp="uk60oxpld2tt">
        <span className="w-6 h-6 rounded-full bg-[#F1F0FD] font-sans text-[11.5px] font-semibold text-[#4F46E5] flex items-center justify-center shrink-0" data-id="element-1059" data-mp="zhfok8xc16fj">
          {index + 1}
        </span>
        <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.5} aria-hidden="true" data-id="element-1060" data-mp="ax2txg1kbjqg" />
      </div>
      <h3 className="font-sans text-[13px] font-bold tracking-[0.04em] uppercase text-[#121A3A] mt-2.5" data-id="element-1061" data-mp="owax0k0ujbzx">{pick(step.label)}</h3>
      <p className="font-sans text-[13px] leading-snug text-[#4A5268] mt-1" data-id="element-1062" data-mp="ktnwluq40ir4">
        {step.lines.map(l => <span key={l.fr} className="block" data-id="element-1063" data-mp="ysdydnnvj6lt">{pick(l)}</span>)}
      </p>

      {step.key === 'act' && <ol className="mt-2.5 flex items-center gap-1.5" aria-label={actFlow.map(pick).join(' → ')} data-id="element-1064" data-mp="tt18eu1phjf0">
          {actFlow.map((f, i) => {
        const A = ACT_ICONS[i];
        return <li key={f.fr} className="flex items-center gap-1.5" data-id="element-1065" data-mp="13n1m3kb1zwzc" data-mpi={i}>
                <span className="inline-flex items-center gap-1 rounded-md bg-[#F1F0FD] px-1.5 py-1 font-sans text-[11.5px] font-medium text-[#4F46E5]" data-id="element-1066" data-mp="1hlhurgsh7xk4">
                  <A className="w-3 h-3" strokeWidth={2} aria-hidden="true" data-id="element-1067" data-mp="3723ltekpho1e" />
                  {pick(f)}
                </span>
                {i < actFlow.length - 1 && <ArrowRightIcon className="w-3 h-3 text-[#8B86F2]" strokeWidth={2} aria-hidden="true" data-id="element-1068" data-mp="3bzdklktzjc1w" />}
              </li>;
      })}
        </ol>}

      <div className="mt-auto pt-3" data-id="element-1069" data-mp="3kti7jt6003c5">
        <div className="flex items-center gap-1.5 rounded-md bg-[#F4FAF6] px-2 py-1" data-id="element-1070" data-mp="3ksgs7chy8uom">
          <CheckCircle2Icon className="w-3.5 h-3.5 text-[#16A34A] shrink-0" strokeWidth={2} aria-hidden="true" data-id="element-1071" data-mp="2o4k9a0z1hna8" />
          <span className="font-sans text-[11.5px] font-medium text-[#2A3150] whitespace-nowrap" data-id="element-1072" data-mp="2t2cchr54wvtq">{pick(step.status)}</span>
          {step.key === 'verify' && <span className="ml-auto flex items-center gap-1" aria-hidden="true" data-id="element-1073" data-mp="323tq92v2klf9">
              {SOURCE_MINI.map((S, i) => <S key={i} className="w-3 h-3 text-[#6D66F0]" strokeWidth={1.8} data-id="element-1074" data-mp="ccx7x2z9amlp" data-mpi={i} />)}
            </span>}
        </div>
      </div>
    </article>;
}
function Connector() {
  return <div className="w-9 shrink-0 flex items-center justify-center" aria-hidden="true" data-id="element-1075" data-mp="rforrqgcnjzt">
      <span className="relative flex items-center w-full" data-id="element-1076" data-mp="mhx0mynj41wo">
        <span className="h-px flex-1 bg-[#B9B5F4]" data-id="element-1077" data-mp="hq57cgczni2u" />
        <span className="w-5 h-5 rounded-full bg-white border border-[#D9D6F8] flex items-center justify-center shrink-0" data-id="element-1078" data-mp="hou36ffms8b7">
          <ArrowRightIcon className="w-3 h-3 text-[#4F46E5]" strokeWidth={2.2} data-id="element-1079" data-mp="8pz19fjmdkdp" />
        </span>
        <span className="h-px flex-1 bg-[#B9B5F4]" data-id="element-1080" data-mp="3qvt3m8cey1i" />
      </span>
    </div>;
}
export function StepsRow({
  pick
}: {
  pick: Pick;
}) {
  return <>
      {/* Wide desktop: six connected steps */}
      <ol className="hidden xl:flex items-stretch gap-1" data-id="element-1081" data-mp="3qvlghuszvghl">
        {atnaSteps.map((s, i) => <li key={s.key} className="flex items-stretch flex-1 min-w-0" data-id="element-1082" data-mp="1t5twnedpst6t" data-mpi={i}>
            <div className="flex-1 min-w-0" data-id="element-1083" data-mp="1t50xayh355yp">
              <StepCard step={s} index={i} pick={pick} data-id="element-1084" data-mp="1p3p64oikccoe" />
            </div>
            {i < atnaSteps.length - 1 && <Connector data-id="element-1085" data-mp="3imfo3d9uwlom" />}
          </li>)}
      </ol>

      {/* Tablet: 3 × 2 grid */}
      <ol className="hidden md:grid xl:hidden grid-cols-3 gap-5" data-id="element-1086" data-mp="3ne7hdvked5ig">
        {atnaSteps.map((s, i) => <li key={s.key} data-id="element-1087" data-mp="39msw1i30os88" data-mpi={i}>
            <StepCard step={s} index={i} pick={pick} data-id="element-1088" data-mp="39nkgpxv2t5jq" />
          </li>)}
      </ol>

      {/* Mobile: vertical sequence */}
      <ol className="md:hidden flex flex-col items-stretch gap-2" data-id="element-1089" data-mp="3dls2560hiv19">
        {atnaSteps.map((s, i) => <li key={s.key} className="flex flex-col items-stretch" data-id="element-1090" data-mp="568b8dnadopd" data-mpi={i}>
            <StepCard step={s} index={i} pick={pick} data-id="element-1091" data-mp="a6m7kyfiza7k" />
            {i < atnaSteps.length - 1 && <ArrowDownIcon className="w-5 h-5 text-[#6D66F0] mx-auto my-2" strokeWidth={1.6} aria-hidden="true" data-id="element-1092" data-mp="3sc8zad636ysc" />}
          </li>)}
      </ol>
    </>;
}